import { FC, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminCostsApi } from "../../http/costs/api";
import { CostNoCarDependency } from "../../http/costs/types";
import { NestedPick, PaginationQueryWithFields } from "../../types";
import { formatCurrency, formatDateNumeric, isWithinLast48h } from "../../types/utils";
import Button from "../ui/Button";
import CustomLink from "../ui/CustomLink";
import Header from "../ui/Header";
import { Action, getDeleteAction, getEditAction, getHistoryAction } from "../ui/ListItemMenu";
import NewItemIcon from "../ui/NewItemIcon";
import Panel from "../ui/Panel";
import Table from "../ui/Table";
import TextColumnFilter from "../ui/columnFilters/TextColumnFilter";
import CostHistoryDialog from "./CostHistoryDialog";
import CreateCostButton from "./CreateCostButton";
import DeleteCostDialog from "./DeleteCostDialog";
import EditCostDialog from "./EditCostDialog";
import EditCostTypeCategoryDialog from "./typeCategories/EditCategoryButton";
import CostTypes from "./types/CostTypes";
import EditCostTypeDialog from "./types/EditCostTypeDialog";

const COST_FIELDS = [
  "id",
  "createdAt",
  "car.id",
  "car.vehicleIdentificationNumber",
  "car.currentPlateNumber",
  "car.lastPlateNumber",
  "type.id",
  "type.name",
  "type.category.id",
  "type.category.name",
  "date",
  "costNet",
  "costGross",
  "versionNumber",
] as const;
type CostField = (typeof COST_FIELDS)[number];
type CostSearch = NestedPick<CostNoCarDependency, CostField>;

const Costs: FC = () => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const costsApi = useAdminCostsApi();
  const filters = useListFilters({ isSearchable: true, prefix: "co" });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());

  const [itemToDelete, setItemToDelete] = useState<CostSearch>();
  const [itemToEdit, setItemToEdit] = useState<CostSearch>();
  const [showCostTypes, setShowCostTypes] = useState(false);
  const [typeIdToShow, setTypeIdToShow] = useState<string>();
  const [categoryIdToShow, setCategoryIdToShow] = useState<string>();
  const { id: carId } = useParams<{ id: string }>();

  const [itemToShowVersions, setItemToShowVersions] = useState<CostSearch>();

  const query: PaginationQueryWithFields<CostSearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: COST_FIELDS,
  };

  const result = costsApi.useAdminCosts(query, aborter.current.signal, carId);
  const [data] = useDataInitializer(result.data, result.error);

  const filterOptions = {
    get: costsApi.getFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const getRowActions = (item: CostSearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.costs.update) {
      actions.push(getEditAction(i18n, () => setItemToEdit(item)));
    }

    actions.push(getHistoryAction(i18n, () => setItemToShowVersions(item)));

    if (userSettings.permissions.costs.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), true));
    }

    return actions;
  };

  const content = (
    <>
      <Table<CostSearch>
        filters={filters}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        buttons={{
          main: userSettings.permissions.costs.insert && <CreateCostButton carId={carId} />,
          secondary: [
            userSettings.permissions.costTypes.read && (
              <Button
                buttonProps={{
                  onClick: () => setShowCostTypes(true),
                }}
              >
                {i18n.t.common.manageCostTypes}
              </Button>
            ),
          ],
        }}
        head={
          <>
            {!carId && (
              <>
                <Table.Th
                  title={i18n.t.plateNumbers.singular}
                  fieldName="cost.car.plateNumber"
                  filters={filterOptions}
                  filter={TextColumnFilter}
                  order={orderOptions}
                />
                <Table.Th
                  title={i18n.t.common.carVin}
                  fieldName="cost.car.vin"
                  filters={filterOptions}
                  filter={TextColumnFilter}
                  order={orderOptions}
                />
              </>
            )}
            <Table.Th
              title={carId ? i18n.t.costTypes.singular : i18n.t.common.type}
              fieldName="cost.type"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th
              title={i18n.t.common.category}
              fieldName="cost.category"
              order={orderOptions}
              filters={filterOptions}
              filter={TextColumnFilter}
            />
            <Table.Th title={i18n.t.common.date} fieldName="cost.date" order={orderOptions} />
            <Table.Th align="right" title={i18n.t.common.costNet} fieldName="cost.cost.net" order={orderOptions} />
            <Table.Th align="right" title={i18n.t.common.costGross} fieldName="cost.cost.gross" order={orderOptions} />
            <th></th>
          </>
        }
        onRowClick={(item, event) => {
          event.preventDefault();
          setItemToEdit(item);
        }}
        renderRow={(item) => (
          <>
            {!carId && (
              <>
                <td>
                  {isWithinLast48h(item.createdAt) && <NewItemIcon />}
                  {userSettings.permissions.cars.read ? (
                    <CustomLink to={`/admin/cars/${item.car.id}`}>
                      {item.car.currentPlateNumber || item.car.lastPlateNumber}
                    </CustomLink>
                  ) : (
                    <>{item.car.currentPlateNumber || item.car.lastPlateNumber}</>
                  )}
                </td>
                <td>
                  {userSettings.permissions.cars.read ? (
                    <CustomLink to={`/admin/cars/${item.car.id}`}>{item.car.vehicleIdentificationNumber}</CustomLink>
                  ) : (
                    item.car.vehicleIdentificationNumber
                  )}
                </td>
              </>
            )}
            <td>
              {!!carId && isWithinLast48h(item.createdAt) && <NewItemIcon />}
              {userSettings.permissions.costTypes.read ? (
                <CustomLink
                  to=""
                  onClick={(e) => {
                    e.preventDefault();
                    setTypeIdToShow(item.type.id);
                  }}
                >
                  {item.type.name}
                </CustomLink>
              ) : (
                item.type.name
              )}
            </td>
            <td>
              {userSettings.permissions.costTypeCategories.read ? (
                <CustomLink
                  to=""
                  onClick={(e) => {
                    e.preventDefault();
                    setCategoryIdToShow(item.type.category.id);
                  }}
                >
                  {item.type.category.name}
                </CustomLink>
              ) : (
                item.type.category.name
              )}
            </td>
            <td>{formatDateNumeric(item.date, i18n.language)}</td>
            <Table.Td noWrap align="right">
              {formatCurrency(item.costNet, i18n.language)}
            </Table.Td>
            <Table.Td noWrap align="right">
              {formatCurrency(item.costGross, i18n.language)}
            </Table.Td>
          </>
        )}
        rowActions={getRowActions}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />

      {!!itemToDelete && (
        <DeleteCostDialog
          costId={itemToDelete.id}
          carId={itemToDelete.car.id}
          onClose={() => setItemToDelete(undefined)}
        />
      )}

      {!!itemToEdit && (
        <EditCostDialog costId={itemToEdit.id} onClose={() => setItemToEdit(undefined)} carId={itemToDelete?.car.id} />
      )}

      {showCostTypes && <CostTypes onClose={() => setShowCostTypes(false)} />}

      {!!itemToShowVersions && (
        <CostHistoryDialog
          itemToShowVersionsId={itemToShowVersions.id}
          itemVersionToShowVersions={itemToShowVersions.versionNumber}
          onClose={() => setItemToShowVersions(undefined)}
        />
      )}

      {!!typeIdToShow && <EditCostTypeDialog costTypeId={typeIdToShow} onClose={() => setTypeIdToShow(undefined)} />}

      {!!categoryIdToShow && (
        <EditCostTypeCategoryDialog
          costTypeCategoryId={categoryIdToShow}
          onClose={() => setCategoryIdToShow(undefined)}
        />
      )}
    </>
  );

  return carId ? (
    content
  ) : (
    <>
      <Header title={i18n.t.costs.plural} />
      <Panel>{content}</Panel>
    </>
  );
};
export default Costs;
